export interface HttpRequest<B = unknown> {
  url: string
  body?: B
  signal?: AbortSignal
}

export class HttpError extends Error {
  readonly status: number
  readonly body?: unknown
  readonly url?: string
  readonly method?: string
  constructor(status: number, body?: unknown, url?: string, method?: string) {
    super(
      method && url
        ? `HTTP ${status} on ${method} ${url}`
        : `HTTP ${status}`,
    )
    this.status = status
    this.body = body
    this.url = url
    this.method = method
    this.name = 'HttpError'
  }
}

export interface HttpGet {
  get<R>(request: HttpRequest): Promise<R>
}

export interface HttpGetWithMeta {
  getWithMeta<R>(request: HttpRequest<never>): Promise<{ data: R; meta?: Record<string, unknown> }>
}

export interface HttpPost {
  post<B, R>(request: HttpRequest<B>): Promise<R>
}

export interface HttpPostWithMeta {
  postWithMeta<B, R>(
    request: HttpRequest<B>,
  ): Promise<{ data: R; meta?: Record<string, unknown> }>
}

export interface HttpPut {
  put<B, R>(request: HttpRequest<B>): Promise<R>
}

export interface HttpPatch {
  patch<B, R>(request: HttpRequest<B>): Promise<R>
}

export interface HttpPatchWithMeta {
  patchWithMeta<B, R>(
    request: HttpRequest<B>,
  ): Promise<{ data: R; meta?: Record<string, unknown> }>
}

export interface HttpPostFormData {
  postFormData<R>(request: HttpRequest<FormData>): Promise<R>
}

export interface HttpPatchFormData {
  patchFormData<R>(request: HttpRequest<FormData>): Promise<R>
}

export interface HttpPostMultipart {
  postMultipart<R>(req: {
    url: string
    form: FormData
    headers?: Record<string, string>
  }): Promise<R>
}

export interface HttpPatchMultipart {
  patchMultipart<R>(req: {
    url: string
    form: FormData
    headers?: Record<string, string>
  }): Promise<R>
}

export interface HttpDelete {
  delete<R>(request: HttpRequest): Promise<R>
}

export interface HttpDeleteWithMeta {
  deleteWithMeta<R>(
    request: HttpRequest,
  ): Promise<{ data: R; meta?: Record<string, unknown> }>
}

export interface HttpGetBlob {
  getBlob(request: HttpRequest): Promise<Blob>
}
